import React from 'react';

import clsx from 'clsx';

import Category from 'components/Category';
import ClickoutWrapper from 'components/ClickoutWrapper';
import Html from 'components/Html';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Logo from 'components/Logo';
import Rating from 'components/Rating';
import Tag from 'components/Tag';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { useEventContext } from 'context/EventContext';
import { getengagementDestination } from 'helpers/reviews';

import classes from './Info.module.scss';

/** @type {import("./types").default} */
const Info = ({
  listing,
  variant,
  component = 'h3',
  listingPosition = 0,
  enableBuyerIntent = false,
}) => {
  const { addEvent } = useEventContext({
    engagementComponent: 'product_card-item',
  });
  const { route } = useAppContext();

  const isSerpPage = route?.includes('serp_');
  const highlyViewedEventString = listing.most_visited
    ? ' | highly-viewed'
    : '';
  const useProductDetailsLink = isSerpPage || !listing.is_premium;
  const primaryLinkUrl = useProductDetailsLink
    ? listing.cta_urls.detail
    : listing.cta_urls.cta;
  const isPrimaryLinkClickout = listing.is_premium && !useProductDetailsLink;

  const getChips = () => {
    const shouldRender = listing.is_category_leader || listing.most_visited;

    if (!shouldRender) {
      return null;
    }

    return (
      <div className={classes.chips}>
        {listing.is_category_leader && (
          <Category.LeaderBadge
            href={listing.cta_urls.category_leaders}
            {...addEvent({
              engagementDetails: 'button_category-leaders',
              engagementDestination: 'go-to_category_leaders',
              name: 'engagement_product_click',
            })}
          />
        )}
        {listing.most_visited && (
          <Tag
            className="HighlyViewedChip"
            color="orange"
            size="small"
            disableRipple
          >
            Highly viewed
            <Icon icon={['fa', 'user-group-solid']} />
          </Tag>
        )}
      </div>
    );
  };
  return (
    <div
      data-testid="listing-item_info"
      className={clsx('Info', classes.root, {
        [classes[`variant-${variant}`]]: variant,
      })}
    >
      <ClickoutWrapper
        url={primaryLinkUrl}
        isClickout={isPrimaryLinkClickout}
        buyerIntentProductId={
          enableBuyerIntent ? listing.global_product_id : undefined
        }
        buyerIntentSourceLocation={
          enableBuyerIntent ? 'listing/info:logo' : undefined
        }
        buyerIntentTrackAdClick={enableBuyerIntent && isPrimaryLinkClickout}
        data-testid="listing-item_logo_product"
        className={classes.logoContainer}
        listingPosition={listingPosition}
        {...addEvent({
          productClickPosition: listingPosition,
          engagementDetails: 'logo_product' + highlyViewedEventString,
          engagementDestination: isPrimaryLinkClickout
            ? 'go-to_vendor-website'
            : 'go-to_product_details',
          name: isPrimaryLinkClickout
            ? 'engagement_product_clickout'
            : 'engagement_product_click',
        })}
      >
        <Logo
          className={classes.logo}
          src={`${listing.logo_url}`}
          alt={`${listing.name} logo`}
          size={80}
        />
      </ClickoutWrapper>
      <div className={classes.info}>
        <div>
          <ClickoutWrapper
            isClickout={isPrimaryLinkClickout}
            url={primaryLinkUrl}
            buyerIntentProductId={
              enableBuyerIntent ? listing.global_product_id : undefined
            }
            buyerIntentSourceLocation={
              enableBuyerIntent ? 'listing/info:name' : undefined
            }
            buyerIntentTrackAdClick={enableBuyerIntent && isPrimaryLinkClickout}
            listingPosition={listingPosition}
            data-testid="listing-item_text-link_product-name"
            {...addEvent({
              productClickPosition: listingPosition,
              engagementDetails:
                'text-link_product-name' + highlyViewedEventString,
              engagementDestination: isPrimaryLinkClickout
                ? 'go-to_vendor-website'
                : 'go-to_product_details',
              name: isPrimaryLinkClickout
                ? 'engagement_product_clickout'
                : 'engagement_product_click',
            })}
          >
            <Link className={classes.title} component={component}>
              <Html>{listing.name}</Html>
              {isPrimaryLinkClickout && (
                <Icon
                  className={classes.clickoutIcon}
                  icon={['far', 'external-link']}
                  size="12"
                />
              )}
            </Link>
          </ClickoutWrapper>
        </div>
        {variant === 'large' ? (
          <div className={classes.details}>
            {getChips()}
            <div className={classes.ratings}>
              <Rating.Link
                className={classes.rating}
                href={listing.cta_urls.reviews}
                average={listing.rating_avg}
                totals={listing.num_reviews}
              />
            </div>
          </div>
        ) : (
          <>
            {getChips()}
            <Rating.Link
              className={classes.rating}
              href={listing.cta_urls.reviews}
              average={listing.rating_avg}
              totals={listing.num_reviews}
              ratings={listing?.reviews?.by_rating}
              ratingsHrefs={listing?.reviews?.by_rating_urls}
              size="small"
              {...(listing.most_visited
                ? addEvent({
                    engagementDetails:
                      'data_overall-rating_stars' + highlyViewedEventString,
                    engagementDestination: getengagementDestination(
                      listing.cta_urls.reviews,
                      {
                        isListingDetail: route === 'listing_detail',
                      }
                    ),
                    name: 'engagement_product_click',
                  })
                : {})}
            />
          </>
        )}
        <Typography className={classes.tagline} variant="tight-xs">
          <Html>{listing.tagline}</Html>
        </Typography>
      </div>
    </div>
  );
};

export default Info;
